import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "../axiosBaseQuery";
import { Price, Response } from "../interfaces";
// import { ReviewLinkType } from "./user.interface";
import {
  DefaultSettings,
  EmailSettingsResponse,
  EmailSettingValue,
  Referral,
  ReviewLink,
  TeamUser,
  User,
} from "./usersTypes";
import { occationApi } from "../occation/occationApi";

export const userApi = createApi({
  reducerPath: "userApi",
  baseQuery: axiosBaseQuery(),
  tagTypes: ["price", "review", "referral", "profile", "defaultSettings", "emailSettings"],
  endpoints: (builder) => ({
    getUserProfile: builder.query<Response<User>, any>({
      query: () => ({
        url: "user",
        method: "GET",
      }),
      providesTags: ["profile"],
    }),

    updateProfile: builder.mutation<
      Response<User>,
      { first_name: string; last_name: string; signature: string }
    >({
      query: (data) => ({
        url: "user",
        method: "PATCH",
        data,
      }),
      invalidatesTags: ["profile"],
    }),

    updateSettings: builder.mutation<Response<User>, any>({
      query: (data) => ({
        url: "user/settings",
        method: "PATCH",
        data,
      }),
      invalidatesTags: ["profile"],
    }),

    getDefaultSettings: builder.query<
      Response<{ default_settings: DefaultSettings }>,
      void
    >({
      query: () => ({
        url: "user/default-settings",
        method: "GET",
      }),
      providesTags: ["defaultSettings"],
    }),

    updateDefaultSettings: builder.mutation<
      Response<{ default_settings: DefaultSettings }>,
      Partial<DefaultSettings>
    >({
      query: (data) => ({
        url: "user/default-settings",
        method: "PATCH",
        data,
      }),
      invalidatesTags: ["defaultSettings"],
      async onQueryStarted(_data, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(occationApi.util.invalidateTags(["occasion"]));
        } catch {
          // ignore; defaultSettings mutation error is handled by the caller
        }
      },
    }),

    disconnectMailbox: builder.mutation<Response<User>, void>({
      query: () => ({
        url: "user/mailbox/disconnect",
        method: "POST",
      }),
      invalidatesTags: ["profile"],
    }),

    connectGoogleMailbox: builder.mutation<
      Response<User>,
      { code: string; scope: string }
    >({
      query: (data) => ({
        url: "user/mailbox/connect/google",
        method: "POST",
        data,
      }),
      invalidatesTags: ["profile"],
    }),

    getPriceList: builder.query<Response<Price[]>, any>({
      query: (credentials) => ({
        url: "price/list",
        method: "GET",
        data: credentials,
      }),
      providesTags: ["price"],
    }),
    getReviewLink: builder.query<Response<ReviewLink[]>, any>({
      query: (credentials) => ({
        url: "user/review-links",
        method: "GET",
        data: credentials,
      }),
      providesTags: ["review"],
    }),
    deleteReviewLink: builder.mutation<Response<ReviewLink[]>, { id: string }>({
      query: (credentials) => ({
        url: "user/review-links/" + credentials.id,
        method: "DELETE",
        data: credentials,
      }),
    }),
    getReferral: builder.query<Response<Referral>, any>({
      query: (credentials) => ({
        url: "user/referral-emailers",
        method: "GET",
        data: credentials,
      }),
      providesTags: ["referral"],
    }),
    patchReferralSettings: builder.mutation<
      Response<ReviewLink[]>,
      | { enabled: boolean }
      | { referral_sender: { on_token_expired: "system" | "wait" | "stop" } }
      | { referral_sender: { sender: "mailbox" | "system" } }
      | Partial<Referral>
    >({
      query: (credentials) => ({
        url: "user/referral-settings",
        method: "PATCH",
        data: credentials,
      }),
      invalidatesTags: ["referral"],
    }),
    getEmailSettings: builder.query<Response<EmailSettingsResponse>, void>({
      query: () => ({
        url: "user/email-settings",
        method: "GET",
      }),
      providesTags: ["emailSettings"],
    }),
    patchEmailSettings: builder.mutation<
      Response<EmailSettingsResponse>,
      {
        reminder?: EmailSettingValue;
        transactional?: EmailSettingValue;
        referral?: EmailSettingValue;
        occasional?: EmailSettingValue;
        send_gift?: EmailSettingValue;
      }
    >({
      query: (data) => ({
        url: "user/email-settings",
        method: "PATCH",
        data,
      }),
      invalidatesTags: ["emailSettings"],
    }),
    updateReviewLinks: builder.mutation<Response<ReviewLink[]>, any>({
      query: (credentials) => ({
        url: "user/review-links",
        method: "POST",
        data: credentials,
      }),
      invalidatesTags: ["review"],
    }),

    updateProfilePicture: builder.mutation<Response<any>, FormData>({
      query: (formData) => ({
        url: "user/updateProfilePicture",
        method: "POST",
        data: formData,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }),
      invalidatesTags: ["profile"],
    }),

    updateSignaturePicture: builder.mutation<Response<string>, FormData>({
      query: (formData) => ({
        url: "user/updateSignaturePicture",
        method: "POST",
        data: formData,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }),
      invalidatesTags: ["profile"],
    }),

    getTeamUsers: builder.query<Response<Array<User>>, void>({
      query: () => ({
        url: "user/teamList",
        method: "GET",
      }),
      providesTags: ["profile"],
    }),

    getTeamUserList: builder.query<Response<Array<TeamUser>>, void>({
      query: () => ({
        url: "user/teamUserList",
        method: "GET",
      }),
      providesTags: ["profile"],
    }),

    inviteTeamMember: builder.mutation<
      Response<{ message: string; expires_at: string }>,
      {
        email: string;
        first_name: string;
        last_name?: string;
        role?: "admin" | "user";
      }
    >({
      query: (data) => ({
        url: "user/invite-team-member",
        method: "POST",
        data,
      }),
      invalidatesTags: ["profile"],
    }),

    getTeamInvitePreview: builder.query<
      Response<{
        email: string;
        first_name: string;
        last_name: string;
        organization_name: string;
        expires_at: string;
      }>,
      string
    >({
      query: (token) => ({
        url: "user/invite/preview",
        method: "GET",
        params: { token },
      }),
    }),

    createUserWithInvite: builder.mutation<
      Response<unknown>,
      {
        first_name: string;
        last_name?: string;
        email: string;
        password: string;
        invite_token: string;
      }
    >({
      query: (data) => ({
        url: "user/create",
        method: "POST",
        data,
      }),
    }),

    sendEnquiry: builder.mutation<
      Response<{ message: string }>,
      { source: string; message: string; subject?: string }
    >({
      query: (data) => ({
        url: "enquiry",
        method: "POST",
        data,
      }),
    }),
  }),
});

export const {
  useGetUserProfileQuery,
  useUpdateProfileMutation,
  useGetPriceListQuery,
  useGetDefaultSettingsQuery,
  useUpdateDefaultSettingsMutation,
  useUpdateReviewLinksMutation,
  useGetReviewLinkQuery,
  useGetReferralQuery,
  usePatchReferralSettingsMutation,
  useGetEmailSettingsQuery,
  usePatchEmailSettingsMutation,
  useUpdateSettingsMutation,
  useDisconnectMailboxMutation,
  useConnectGoogleMailboxMutation,
  useDeleteReviewLinkMutation,
  useUpdateProfilePictureMutation,
  useUpdateSignaturePictureMutation,
  useGetTeamUsersQuery,
  useGetTeamUserListQuery,
  useInviteTeamMemberMutation,
  useGetTeamInvitePreviewQuery,
  useCreateUserWithInviteMutation,
  useSendEnquiryMutation,
} = userApi;
